/**
 * FloorPlansHero — Weidner Via6 page hero for /floor-plans.
 *
 * Replaces the inherited rise HeroSolidBG section. Renders a hero IMAGE
 * with the page title left-aligned at the bottom of the section, per
 * the brand brief. The visual is shared via `PageImageHeroBody`; this
 * file owns the slot registration, the editable contentSchema, and the
 * hidden literal-path anchors the schema linter relies on.
 *
 * Each per-page *Hero in this theme follows this same shape — see
 * `_PageImageHeroBody.tsx` for the rendering helper and any of the
 * sibling files for the structural pattern.
 */

import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { PageImageHeroBody } from './_PageImageHeroBody';

// Background image definition. Mirrors the schema's slotGroups entry
// below — the shared body uses this same literal at runtime so the
// editor sidebar and the rendered slot stay in lockstep.
const bgDef = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Floor plans hero image',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

export function FloorPlansHero() {
  return (
    <>
      <PageImageHeroBody
        titlePath="floorplanshero.title"
        bgPath="floorplanshero.bg_image"
        bgDef={bgDef}
        defaultTitle="Floor Plans"
        keywords={['floorplan', 'floor plan', 'apartment', 'interior']}
        pageHint="floorplanshero"
        altText="Floor plans hero image"
      />

      {/* Schema-lint anchors — hidden, never interactive.
          The visible JSX above lives inside `PageImageHeroBody`, which
          uses the dynamic paths above. The schema linter only scans this
          file's source for literal `path=` / `groupPath=` strings, so we
          duplicate them here as hidden anchors. See
          reference/shared-component-pattern.md for the rationale. */}
      <RichTextContent
        path="floorplanshero.title"
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableSlotGroup
        groupPath="floorplanshero.bg_image"
        definition={bgDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorPlansHero',
  displayName: 'Floor Plans Hero',
  tokens: {
    'floorplanshero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: 'Floor Plans',
    },
  },
  slotGroups: {
    'floorplanshero.bg_image': {
      label: 'Background Image',
      fields: {
        image: {
          label: 'Image',
          type: 'image',
          defaultField: 'property_featured_image',
        },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultValue: 'Floor plans hero image',
        },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 0,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    },
  },
};

(FloorPlansHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FloorPlansHero;
